// useOnboarding — context + step navigation for the first-run wizard.
//
// Modeled on /opt/4bix-source/src/components/onboarding/use-onboarding.ts but
// simpler: we don't have the wizard-as-signup case (Go2Fix uses email-OTP /
// Google), so this is purely post-auth orientation.
//
// Persistence: completion state currently lives in localStorage keyed per
// userId. A backend `me.onboardingCompleted` field is the next step
// (audit doc 19 §3.7) — when that lands, this file's `complete()` will fire
// the GraphQL mutation and the gate will read from `me`.
//
// Track resolution: getStepsForRole(role) returns the ordered list of
// SlideName values for that role. Render the matching slide component for
// the current step in the orchestrator.

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

export type SlideName =
  | 'splash'
  | 'language'
  | 'welcome'
  | 'notify'
  | 'client-address'
  | 'client-interests'
  | 'worker-skills'
  | 'worker-documents'
  | 'worker-personality'
  | 'company-profile'
  | 'company-team'
  | 'company-services'
  | 'ready'

type Role = 'CLIENT' | 'WORKER' | 'COMPANY' | 'ADMIN' | 'GLOBAL_ADMIN'

export interface OnboardingData {
  locale?: string
  notificationPermission?: NotificationPermission
  // Client
  firstAddressId?: string
  serviceInterests?: string[]
  // Worker
  skills?: string[]
  // Company
  invitedTeamMembers?: number
  serviceOfferings?: string[]
}

interface OnboardingContextValue {
  step: number
  steps: SlideName[]
  current: SlideName
  direction: 'forward' | 'back'
  data: OnboardingData
  updateData: (patch: Partial<OnboardingData>) => void
  next: () => void
  prev: () => void
  skip: () => void
  complete: () => void
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined)

const COMMON_OPEN: SlideName[] = ['splash', 'language', 'welcome', 'notify']

export function getStepsForRole(role: Role | null | undefined): SlideName[] {
  switch (role) {
    case 'WORKER':
      return [
        ...COMMON_OPEN,
        'worker-skills',
        'worker-documents',
        'worker-personality',
        'ready',
      ]
    case 'COMPANY':
      return [
        ...COMMON_OPEN,
        'company-profile',
        'company-team',
        'company-services',
        'ready',
      ]
    case 'ADMIN':
    case 'GLOBAL_ADMIN':
      return [...COMMON_OPEN, 'ready']
    case 'CLIENT':
    default:
      return [
        ...COMMON_OPEN,
        'client-address',
        'client-interests',
        'ready',
      ]
  }
}

export function onboardingStorageKey(userId: string) {
  return `go2fix:onboarding-completed-${userId}`
}

export function isOnboardingCompleted(userId: string): boolean {
  try {
    return localStorage.getItem(onboardingStorageKey(userId)) === '1'
  } catch {
    return false
  }
}

export function markOnboardingCompleted(userId: string) {
  try {
    localStorage.setItem(onboardingStorageKey(userId), '1')
  } catch {
    /* private mode — wizard will fire again, that's acceptable */
  }
}

interface ProviderProps {
  role: Role | null | undefined
  userId: string
  onCompleted: () => void
  children: React.ReactNode
}

export function OnboardingProvider({ role, userId, onCompleted, children }: ProviderProps) {
  const steps = useMemo(() => getStepsForRole(role), [role])
  const [step, setStep] = useState(0)
  const [direction, setDirection] = useState<'forward' | 'back'>('forward')
  const [data, setData] = useState<OnboardingData>({})
  const completedRef = useRef(false)

  const updateData = useCallback((patch: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...patch }))
  }, [])

  const finish = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    markOnboardingCompleted(userId)
    // TODO(audit-19 §3.7): also fire `mutation completeOnboarding(input)` so
    // the server-side `users.onboarding_completed` flag is set and the wizard
    // doesn't re-fire on a different device. Until that's wired, localStorage
    // is the source of truth.
    onCompleted()
  }, [userId, onCompleted])

  const next = useCallback(() => {
    setDirection('forward')
    setStep((s) => {
      const target = s + 1
      if (target >= steps.length) {
        finish()
        return s
      }
      return target
    })
  }, [steps.length, finish])

  const prev = useCallback(() => {
    setDirection('back')
    setStep((s) => Math.max(0, s - 1))
  }, [])

  const skip = useCallback(() => {
    next()
  }, [next])

  const complete = useCallback(() => {
    finish()
  }, [finish])

  const value: OnboardingContextValue = {
    step,
    steps,
    current: steps[step] ?? 'ready',
    direction,
    data,
    updateData,
    next,
    prev,
    skip,
    complete,
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext)
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider')
  return ctx
}
